import { chain } from 'underscore';
import { useCallback } from 'react';
import { Activity, DirectLine } from 'botframework-directlinejs';
import axios from 'axios';
import { IAdsCopilotProps } from '../types';

export function getOAuthCardResourceUri(activity: any): string | null {
  if (activity
    && activity.attachments
    && activity.attachments[0]
    && activity.attachments[0].contentType === 'application/vnd.microsoft.card.oauth'
    && activity.attachments[0].content.tokenExchangeResource) {
    // asking for token exchange with AAD
    return activity.attachments[0].content.tokenExchangeResource.uri as string;
  }
  return null;
}

export type FetchAllActivities = (conversationId: string, token: string) => Promise<Activity[]>;
export const defaultFetchAllActivities: FetchAllActivities = (conversationId, token) => {
  const url = `https://directline.botframework.com/v3/directline/conversations/${conversationId}/activities`;
  return axios.get<void, { data: { activities: Activity[] } }>(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }).then((res: { data: { activities: Activity[] } }) => res.data.activities);
};

export const useSSO = (params : {
  onReceiveActivity?: IAdsCopilotProps['onReceiveActivity'],
  botServiceConfig: Pick<IAdsCopilotProps['botServiceConfig'], 'username' | 'authToken' | 'fetchOAuthToken'>
  fetchAllActivities?: FetchAllActivities,
}) => {
  const { onReceiveActivity, botServiceConfig: { authToken, fetchOAuthToken, username }, fetchAllActivities = defaultFetchAllActivities } = params;
  const onReceiveActivityWithSSO = useCallback((activity: Activity, currentActivities: Activity[], directLine: DirectLine) => {
    const isAIMessage = activity.from.name !== username;
    const resourceUri = getOAuthCardResourceUri(activity);
    if (isAIMessage && resourceUri != null) {
      const conversationId = activity.conversation?.id;
      const dlToken = (directLine as any).token;

      fetchAllActivities(conversationId!, dlToken).then((activities) => {
        const lastAct = activities[activities.length - 1];

        // send token exchange invoke activity to bot only when OAuthCard is the last message sent by bot
        if (lastAct.id === activity.id) {
          const content = chain(activity).result('attachments').result('0').result('content')
            .value() as any;
          const id = chain(content)
            .result('tokenExchangeResource')
            .result('id')
            .value() as any;
          const connectionName = chain(content)
            .result('connectionName')
            .value();

          const oAuthTokenPromise = fetchOAuthToken ? fetchOAuthToken() : Promise.resolve({ token: authToken });
          oAuthTokenPromise.then(({ token: oAuthToken }) => {
            directLine?.postActivity({
              type: 'invoke',
              name: 'signin/tokenExchange',
              from: { name: username },
              value: {
                id,
                connectionName,
                token: oAuthToken,
              },
            } as any).subscribe();
          });
        }
      });
      return currentActivities;
    } if (onReceiveActivity) {
      return onReceiveActivity(activity, currentActivities, directLine);
    }

    return currentActivities;
  }, [authToken, fetchAllActivities, fetchOAuthToken, onReceiveActivity, username]);

  return { onReceiveActivity: onReceiveActivityWithSSO };
};
